import React from "react";
import {
  Alert,
  Button,
  Card,
  Modal,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { getCurrentUser } from "@/actions/current-user";
import { MACHINE_ID } from "@/constants/authentication";
import { modalConfirmDialog } from "@modules/helpers";
import { getCurrentUser as selectCurrentUser } from "@modules/utils/value-selector";
import {
  buildTelegramBindDeepLink,
  formatCountdown,
  formatTelegramBindCommand,
  getTelegramBotBaseUrl,
  getTelegramBotName,
  isTelegramLinked,
} from "@modules/user-profile/helpers/telegram";
import {
  fetchTelegramLinkStatus,
  generateTelegramBindCode,
  pickTelegramFieldsFromUser,
  unbindTelegram,
  updateTelegramSettings,
} from "@/services/telegram";

const { Text, Paragraph, Title } = Typography;
const POLL_INTERVAL_MS = 4000;
const ADMIN_BOT_URL_HINT =
  "Ask your administrator to set the Telegram bot URL in Settings → Configuration → General.";

function TelegramNotifications() {
  const dispatch = useDispatch();
  const machineId = localStorage.getItem(MACHINE_ID);
  const reduxUser = useSelector((state) => selectCurrentUser(state)?.user);

  const [telegramStatus, setTelegramStatus] = React.useState(() =>
    pickTelegramFieldsFromUser(reduxUser)
  );
  const [bindModalVisible, setBindModalVisible] = React.useState(false);
  const [bindCode, setBindCode] = React.useState(null);
  const [expiresAt, setExpiresAt] = React.useState(null);
  const [telegramBotUrl, setTelegramBotUrl] = React.useState(null);
  const [bindCodeLoading, setBindCodeLoading] = React.useState(false);
  const [checkingLink, setCheckingLink] = React.useState(false);
  const [toggleLoading, setToggleLoading] = React.useState(false);
  const [unlinkLoading, setUnlinkLoading] = React.useState(false);
  const [remainingMs, setRemainingMs] = React.useState(null);
  const expiredMessageShown = React.useRef(false);
  const statusInitializedRef = React.useRef(Boolean(reduxUser?.id));

  const linked = isTelegramLinked(telegramStatus);
  const telegramUsername = telegramStatus.telegramUsername;
  const notificationsEnabled = telegramStatus.telegramNotificationsEnabled;

  const botName = getTelegramBotName(telegramBotUrl);
  const subDomain = window.location.host.split(".")[0];
  const botBaseUrl = getTelegramBotBaseUrl(telegramBotUrl);
  const botOpenUrl = buildTelegramBindDeepLink(telegramBotUrl, bindCode, subDomain);
  const bindCommand = bindCode ? formatTelegramBindCommand(bindCode) : "";

  const syncReduxCurrentUser = () => dispatch(getCurrentUser(machineId));

  React.useEffect(() => {
    if (statusInitializedRef.current || !reduxUser?.id) {
      return;
    }
    statusInitializedRef.current = true;
    setTelegramStatus(pickTelegramFieldsFromUser(reduxUser));
  }, [reduxUser]);

  React.useEffect(() => {
    if (!expiresAt || !bindModalVisible) {
      setRemainingMs(null);
      return;
    }
    const tick = () => {
      const ms = new Date(expiresAt).getTime() - Date.now();
      setRemainingMs(ms > 0 ? ms : 0);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt, bindModalVisible]);

  const resetBindState = React.useCallback(() => {
    setBindCode(null);
    setExpiresAt(null);
    setTelegramBotUrl(null);
    expiredMessageShown.current = false;
  }, []);

  const completeBind = React.useCallback(
    (status) => {
      setTelegramStatus(status);
      message.success("Telegram account linked successfully");
      setBindModalVisible(false);
      resetBindState();
      dispatch(getCurrentUser(machineId));
    },
    [dispatch, machineId, resetBindState]
  );

  const checkBindStatus = React.useCallback(async () => {
    if (!bindModalVisible) {
      return false;
    }
    try {
      const status = await fetchTelegramLinkStatus(machineId);
      if (isTelegramLinked(status)) {
        completeBind(status);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [bindModalVisible, completeBind, machineId]);

  React.useEffect(() => {
    if (!bindModalVisible || linked) {
      return;
    }
    checkBindStatus();
    const intervalId = setInterval(checkBindStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [bindModalVisible, linked, checkBindStatus]);

  React.useEffect(() => {
    if (
      bindModalVisible &&
      bindCode &&
      !linked &&
      remainingMs === 0
    ) {
      if (!expiredMessageShown.current) {
        expiredMessageShown.current = true;
        message.warning("Code expired, generate a new one");
      }
    } else if (remainingMs > 0) {
      expiredMessageShown.current = false;
    }
  }, [bindModalVisible, bindCode, linked, remainingMs]);

  const requestBindCode = async () => {
    setBindCodeLoading(true);
    expiredMessageShown.current = false;
    try {
      if (linked) {
        message.info("Your Telegram account is already linked.");
        return;
      }
      const response = await generateTelegramBindCode();
      setBindCode(response.bindCode);
      setExpiresAt(response.expiresAt);
      setTelegramBotUrl(response.telegramBotUrl || null);
      setBindModalVisible(true);
    } catch (error) {
      message.error(error?.message || "Failed to generate bind code");
    } finally {
      setBindCodeLoading(false);
    }
  };

  const onManualLinkCheck = async () => {
    setCheckingLink(true);
    try {
      const linkedNow = await checkBindStatus();
      if (!linkedNow) {
        message.info("Telegram is not linked yet. Complete /bind in the bot and try again.");
      }
    } finally {
      setCheckingLink(false);
    }
  };

  const copyText = async (text, successMessage) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      message.success(successMessage);
    } catch (error) {
      message.error("Could not copy to clipboard");
    }
  };

  const onToggleNotifications = async (checked) => {
    setToggleLoading(true);
    try {
      const response = await updateTelegramSettings(checked);
      setTelegramStatus(pickTelegramFieldsFromUser(response?.user || response));
      syncReduxCurrentUser();
      message.success(
        checked
          ? "Telegram notifications enabled"
          : "Telegram notifications disabled"
      );
    } catch (error) {
      message.error(error?.message || "Failed to update Telegram settings");
    } finally {
      setToggleLoading(false);
    }
  };

  const onUnlink = () => {
    modalConfirmDialog(
      "unlink your Telegram account",
      "You will stop receiving Telegram notifications until you link again.",
      async () => {
        setUnlinkLoading(true);
        try {
          const response = await unbindTelegram();
          setTelegramStatus(pickTelegramFieldsFromUser(response?.user || response));
          syncReduxCurrentUser();
          message.success("Telegram account unlinked");
        } catch (error) {
          message.error(error?.message || "Failed to unlink Telegram");
          throw error;
        } finally {
          setUnlinkLoading(false);
        }
      },
      null,
      "480px",
      "danger"
    );
  };

  const closeBindModal = () => {
    setBindModalVisible(false);
    resetBindState();
  };

  const statusLabel = linked
    ? telegramUsername
      ? `Connected as @${telegramUsername}`
      : "Connected"
    : "Not connected";

  return (
    <Card bordered={false} style={{ margin: 24 }}>
      <Title level={5}>Telegram notifications</Title>
      <Paragraph type="secondary">
        Link Telegram to get ERP alerts on your phone. When enabled, you receive
        the same notifications as in the browser (in-app and push). Browser
        notifications work independently of Telegram.
      </Paragraph>

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Text strong>Status: </Text>
          {linked ? (
            <Text type="success">
              <CheckCircleOutlined /> {statusLabel}
            </Text>
          ) : (
            <Text type="secondary">{statusLabel}</Text>
          )}
        </div>

        {!linked && (
          <Button
            type="primary"
            icon={<LinkOutlined />}
            loading={bindCodeLoading}
            onClick={requestBindCode}
          >
            Link Telegram
          </Button>
        )}

        {linked && (
          <>
            <div>
              <Switch
                checked={notificationsEnabled}
                loading={toggleLoading}
                onChange={onToggleNotifications}
              />
              <Text style={{ marginLeft: 8 }}>Send notifications to Telegram</Text>
            </div>
            {!notificationsEnabled && (
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Browser and in-app notifications still work. Telegram is an
                optional extra channel.
              </Paragraph>
            )}
            <Button danger loading={unlinkLoading} onClick={onUnlink}>
              Unlink
            </Button>
          </>
        )}
      </Space>

      <Modal
        title="Link Telegram"
        visible={bindModalVisible}
        onCancel={closeBindModal}
        footer={[
          <Button key="close" onClick={closeBindModal}>
            Close
          </Button>,
          <Button
            key="refresh"
            loading={checkingLink}
            onClick={onManualLinkCheck}
          >
            I&apos;ve linked
          </Button>,
          <Button
            key="regenerate"
            type="primary"
            loading={bindCodeLoading}
            onClick={requestBindCode}
          >
            Generate new code
          </Button>,
        ]}
        width={520}
        maskClosable={false}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text type="secondary">Bind code</Text>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 8,
              }}
            >
              <Title level={2} style={{ margin: 0, letterSpacing: 4 }}>
                {bindCode}
              </Title>
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyText(bindCode, "Bind code copied")}
              />
            </div>
            <Text type="secondary">
              Expires in: {formatCountdown(remainingMs)}
              {remainingMs === 0 && " — generate a new code if needed."}
            </Text>
          </div>

          {!botBaseUrl && (
            <Alert type="warning" showIcon message={ADMIN_BOT_URL_HINT} />
          )}

          {botOpenUrl && (
            <div>
              <a
                href={botOpenUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open {botName || "Telegram bot"}
              </a>
              {botOpenUrl.includes("?start=") && (
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  Opens Telegram with your bind code. You can also send{" "}
                  <Text code>{bindCommand}</Text> manually.
                </Paragraph>
              )}
            </div>
          )}

          <div>
            <Text strong>Steps</Text>
            <ol style={{ paddingLeft: 20, marginTop: 8, marginBottom: 0 }}>
              <li>
                Open Telegram
                {botOpenUrl ? (
                  <>
                    {" — "}
                    <a
                      href={botOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {botName || "Open bot"}
                    </a>
                  </>
                ) : (
                  <> — {ADMIN_BOT_URL_HINT}</>
                )}
              </li>
              <li>
                Send to the bot:{" "}
                <Text code copyable={{ text: bindCommand }}>
                  {bindCommand}
                </Text>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyText(bindCommand, "Command copied")}
                />
              </li>
              <li>Wait for the bot success message.</li>
              <li>
                This page will update automatically, or click &quot;I&apos;ve
                linked&quot;.
              </li>
            </ol>
          </div>

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            <SendOutlined /> Generating a new code invalidates the previous one.
          </Paragraph>
        </Space>
      </Modal>
    </Card>
  );
}

export default TelegramNotifications;
